import axiosClient from './axiosClient';
import { IApiBaseResponse } from '~/utils/interface/common';

// Interface cho User Management
export interface IUser {
  id: string;
  name: string | null;
  email: string;
  phoneNumber?: string | null;
  role: string;
  status: 'ACTIVE' | 'INACTIVE';
  avatar?: string;
  color?: string | null;
}

export interface IParamsGetUsers {
  page?: number;
  limit?: number;
  search?: string;
  searchFields?: Array<string>;
  sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
}

export interface IResponseGetUsers {
  responses: Array<IUser>;
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface ICreateUserPayload {
  email: string;
  role: string;
  status?: 'ACTIVE' | 'INACTIVE';
  profile: {
    name: string;
    phoneNumber: string;
    color: string;
  };
}

export interface IUpdateUserPayload {
  email: string;
  role: string;
  status?: 'ACTIVE' | 'INACTIVE';
  profile: {
    name: string;
    phoneNumber: string;
    color: string;
  };
}

export interface IDeleteUserPayload {
  ids: Array<string>;
}

export interface ICreateUserResponse {
  id: string;
}

export interface ISalesRep {
  id: string;
  createdAt: string;
  updatedAt: string;
  email: string;
  username: string;
  role: string;
  status: string;
  enrolled: string;
  insideSales: boolean;
  profile: {
    id: string;
    createdAt: string;
    updatedAt: string;
    firstName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
    address: string | null;
    postalCode: string | null;
    city: string | null;
    state: string | null;
    employeeID: string | null;
    performance: string | null;
    gender: string | null;
    nationality: string | null;
    joinDate: string;
    color: string;
    avatar: string | null;
  } | null;
}

export interface IResponseGetSalesReps {
  responses: Array<ISalesRep>;
  pagination: {
    totalItems: number;
    totalPage: number;
    page: number;
    limit: number;
    skip: number;
  };
}

export interface IEmployee {
  id: number;
  email: string;
  username: string;
  role: string;
  status: 'ACTIVE' | 'INACTIVE';
  enrolled: string;
  insideSales: boolean;
  createdAt: string;
  updatedAt: string;
  profile: {
    id: number;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    address: string;
    employeeID: string;
    performance: string;
    postalCode: string | null;
    city: string;
    state: string | null;
    gender: string;
    nationality: string | null;
    color: string;
    createdAt: string;
    updatedAt: string;
    avatar: {
      id: number;
      url: string;
      createdAt: string;
      updatedAt: string;
    } | null;
  };
}

export interface IParamsGetEmployees {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortType?: 'ASC' | 'DESC';
  textSearch?: string;
  role?: string;
  status?: 'ACTIVE' | 'INACTIVE';
}

export interface IResponseGetEmployees {
  responses: Array<IEmployee>;
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPage: number;
  };
}

const userManagementApi = {
  // GET /api/settings/user-manager
  getUsers(params?: IParamsGetUsers) {
    const url = `/api/settings/user-manager`;
    return axiosClient.get<IApiBaseResponse<IResponseGetUsers>>(url, { params });
  },
  // POST /api/employees/new
  createUser(payload: ICreateUserPayload) {
    const url = `/api/employees/new`;
    return axiosClient.post<IApiBaseResponse<ICreateUserResponse>>(url, payload);
  },
  // PUT /api/employees/:id
  updateUser(id: string, payload: IUpdateUserPayload) {
    const url = `/api/employees/${id}`;
    return axiosClient.put<IApiBaseResponse<ICreateUserResponse>>(url, payload);
  },
  // DELETE /api/employees
  deleteUsers(payload: IDeleteUserPayload) {
    const url = `/api/employees`;
    return axiosClient.delete<IApiBaseResponse<null>>(url, { data: payload });
  },
  // GET /api/employees/sales/list
  getSalesReps() {
    const url = `/api/employees/sales/list`;
    return axiosClient.get<IApiBaseResponse<IResponseGetSalesReps>>(url);
  },
  // GET /api/employees
  getEmployees(params?: IParamsGetEmployees) {
    const url = `/api/employees`;
    return axiosClient.get<IApiBaseResponse<IResponseGetEmployees>>(url, { params });
  },
};

export default userManagementApi;
